import { useState, type JSX } from 'react';
import { useBrand } from '../brand/context.js';
import { charactersApi, type CharacterDto, type RenditionDto } from '../api/library.js';
import { useI18n } from '../i18n/context.js';
import { ShootingChecklist } from './PhotoSlots.js';

/**
 * Выход из тупика «не похож» (01-SPEC.md §5.5).
 *
 * После третьей попытки крутить генерацию бессмысленно: почти всегда виноваты
 * исходные кадры. Вместо кнопки «ещё раз» — разбор загруженных фото, чек-лист
 * съёмки и «Позвать человека». Кнопка новой попытки возвращается сама, как
 * только состав фотографий изменится: тогда пробовать снова снова осмысленно.
 */
export function PhotoAdvice({
  character,
  rendition,
  onFixPhotos,
}: {
  character: CharacterDto;
  rendition: RenditionDto;
  onFixPhotos(): void;
}): JSX.Element {
  const { t } = useI18n();
  const { support } = useBrand();
  const labels = t.library.deadlock;
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  return (
    <section className="kitup-card grid gap-3 border border-amber-300 p-4">
      <h3 className="font-semibold">{labels.title}</h3>
      <p className="text-sm text-[var(--kitup-color-muted)]">{labels.intro}</p>

      <h4 className="text-sm font-semibold">{labels.photosTitle}</h4>
      {character.photos.length === 0 ? (
        <p className="text-sm text-[var(--kitup-color-muted)]">{labels.noPhotos}</p>
      ) : (
        <ul className="grid gap-2 text-sm">
          {character.photos.map((photo) => {
            const issues = character.photoReview.issues.filter(
              (issue) => issue.assetId === photo.assetId,
            );
            return (
              <li key={photo.assetId} className="flex items-center gap-3">
                <img src={photo.url} alt="" className="h-12 w-12 rounded object-cover" />
                <span>{t.library.photos.slots[photo.slot]}</span>
                <span className="text-[var(--kitup-color-muted)]">
                  {issues.map((issue) => t.library.photos.issues[issue.code]).join(' · ')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {character.photoReview.missingSlots.map((slot) => (
        <p key={slot} className="text-sm text-amber-800">
          {t.library.photos.slots[slot]} — {t.library.photos.issues.slot_missing}
        </p>
      ))}

      <ShootingChecklist />

      <div className="flex flex-wrap gap-3">
        <button type="button" className="kitup-secondary-button" onClick={onFixPhotos}>
          {labels.fixPhotos}
        </button>
      </div>

      <label className="grid gap-1.5 text-sm font-medium">
        {labels.commentLabel}
        <textarea
          className="kitup-input min-h-20 resize-y"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </label>
      <button
        type="button"
        className="kitup-secondary-button"
        disabled={state === 'sending' || state === 'sent'}
        onClick={() => {
          setState('sending');
          void charactersApi
            .supportRequest(character.id, { renditionId: rendition.id, comment })
            .then(() => setState('sent'))
            .catch(() => setState('error'));
        }}
      >
        {labels.callHuman}
      </button>
      {state === 'sent' ? (
        <p role="status" className="text-sm">
          {labels.sent}
          {support.replyWithinHours ? ` ${t.common.replyWithin(support.replyWithinHours)}` : null}
        </p>
      ) : null}
      {state === 'error' ? (
        <p role="alert" className="text-sm text-red-700">
          {labels.failed}
        </p>
      ) : null}
    </section>
  );
}
